import Fuse from 'fuse.js'
import type { Technique } from '@/lib/types'
import type { ToolDbEntry } from '@/lib/types'
import { getAllTechniques } from '@/lib/content'
import { getAllTools, toolUrl } from '@/lib/tools'
import { techniqueUrl } from '@/lib/content'
import type { Lang } from '@/i18n'
import { getLang } from '@/i18n'

export type SearchHit =
  | { type: 'technique'; item: Technique; href: string }
  | { type: 'tool'; item: ToolDbEntry; href: string }

const fuseTechByLang: Partial<Record<Lang, Fuse<Technique>>> = {}
const fuseToolsByLang: Partial<Record<Lang, Fuse<ToolDbEntry>>> = {}

export function getSearchEngine(lang?: Lang) {
  const l = lang || getLang()
  if (!fuseTechByLang[l]) {
    fuseTechByLang[l] = new Fuse(getAllTechniques(l), {
      keys: [
        { name: 'title', weight: 3 },
        { name: 'tags', weight: 2.4 },
        { name: 'category', weight: 2 },
        { name: 'subcategory', weight: 1.6 },
        { name: 'slug', weight: 1.5 },
        { name: 'summary', weight: 1.1 },
        { name: 'mitre', weight: 0.9 },
        { name: 'owasp', weight: 0.9 },
        { name: 'attack', weight: 0.45 },
        { name: 'defense', weight: 0.45 },
        { name: 'detection', weight: 0.4 },
        { name: 'body', weight: 0.25 },
        { name: 'sources', weight: 0.2 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      includeMatches: true,
      minMatchCharLength: 2,
    })
  }
  return fuseTechByLang[l]!
}

function getToolSearchEngine(lang?: Lang) {
  const l = lang || getLang()
  if (!fuseToolsByLang[l]) {
    fuseToolsByLang[l] = new Fuse(getAllTools(l), {
      keys: [
        { name: 'name', weight: 3 },
        { name: 'aliases', weight: 2.5 },
        { name: 'description', weight: 1.4 },
        { name: 'tags', weight: 1.2 },
        { name: 'kind', weight: 1.1 },
        { name: 'source', weight: 0.9 },
        { name: 'category', weight: 1 },
        { name: 'package', weight: 0.8 },
        { name: 'slug', weight: 1.2 },
        { name: 'useCases', weight: 0.9 },
      ],
      threshold: 0.36,
      ignoreLocation: true,
      minMatchCharLength: 2,
    })
  }
  return fuseToolsByLang[l]!
}

export function searchTechniques(query: string, lang?: Lang) {
  const q = query.trim()
  if (!q) return []
  return getSearchEngine(lang).search(q).slice(0, 40)
}

export function searchAll(query: string, lang?: Lang): SearchHit[] {
  const l = lang || getLang()
  const q = query.trim()
  if (!q) return []
  const techHits = getSearchEngine(l)
    .search(q)
    .slice(0, 24)
    .map((r) => ({
      type: 'technique' as const,
      item: r.item,
      href: techniqueUrl(r.item, l),
    }))
  const toolHits = getToolSearchEngine(l)
    .search(q)
    .slice(0, 16)
    .map((r) => ({
      type: 'tool' as const,
      item: r.item,
      href: toolUrl(r.item.slug, l),
    }))
  return [...techHits, ...toolHits]
}
